import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import useFormContext from 'dpl/shared/form/hooks/useFormContext';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import Badge from 'dpl/common/design_system/Badge';
import LitterParentsVennImages from 'dpl/breeder_dashboard/components/LitterParentsVennImages';

// Distinct from '' so the NON_EMPTY constraint can tell "no preference" from unanswered.
const NO_PREFERENCE_VALUE = 'no_preference';
const DEFAULT_OPTIONS_VISIBLE = 6;
const VENN_IMAGE_PROPS = {
  isOverlapping: true,
  isBordered: true,
  width: '64px',
  height: '64px'
};
// Parentless litters show the dog placeholder instead of an empty slot.
const PARENTLESS_FALLBACK_IMAGE = { src: null, alt: 'Litter' };

const LitterOptionPropType = PropTypes.shape({
  value: PropTypes.string,
  label: PropTypes.string,
  subtext: PropTypes.string,
  ready_date: PropTypes.string,
  parents: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      gender: PropTypes.string,
      profile_photo_url: PropTypes.string
    })
  )
});

function LitterSelectorQuestionOption({
  isOptionSelected,
  setSelectedLitter,
  option
}) {
  return (
    <ClickableDiv
      className={classnames(
        'LitterSelectorQuestionOption h-100 w-100 hover-b--midnight db bg-white br12 box-shadow-hover ba pa4 tc',
        isOptionSelected(option) ? 'b--stone-700' : 'b--default'
      )}
      onClick={() => setSelectedLitter(option.value)}
    >
      <div className="flex justify-center mb3">
        <LitterParentsVennImages
          litterParents={option.parents}
          vennImageProps={VENN_IMAGE_PROPS}
          fallbackImage={PARENTLESS_FALLBACK_IMAGE}
        />
      </div>
      <h6 className="lh-title font-20 mb1">{option.label}</h6>
      <p className="font-14 secondary mb2">{option.subtext}</p>
      {option.ready_date && (
        <Badge size="xsmall" color="teal">
          {`Ready ${option.ready_date}`}
        </Badge>
      )}
    </ClickableDiv>
  );
}

LitterSelectorQuestionOption.propTypes = {
  option: LitterOptionPropType.isRequired,
  setSelectedLitter: PropTypes.func.isRequired,
  isOptionSelected: PropTypes.func.isRequired
};

export default function LitterSelectorQuestion({ name, options }) {
  const { setValue, formFieldValue } = useFormContext(name);
  const [displayHiddenOptions, setDisplayHiddenOptions] = useState(false);
  const hiddenOptionsAvailable = options.length > DEFAULT_OPTIONS_VISIBLE;

  function isOptionSelected(opt) {
    return opt.value === formFieldValue;
  }

  function setSelectedLitter(litterId) {
    setValue(name, litterId);
  }

  function onShowMoreClick() {
    setDisplayHiddenOptions(true);
  }

  const visibleLitters = displayHiddenOptions
    ? options
    : options.slice(0, DEFAULT_OPTIONS_VISIBLE);

  return (
    <div className="LitterSelectorQuestion QuestionLayout">
      <ul className="LitterSelectorQuestion__container flex flex-wrap justify-center tl mb6 mb8-md">
        {visibleLitters.map(option => (
          <li
            key={option.value}
            data-test-id={option.value}
            className="LitterSelectorQuestion__option w-100 w-auto-m mb6 mb4-md mh2-md"
          >
            <LitterSelectorQuestionOption
              option={option}
              isOptionSelected={isOptionSelected}
              setSelectedLitter={setSelectedLitter}
            />
          </li>
        ))}
      </ul>
      {!displayHiddenOptions && hiddenOptionsAvailable && (
        <div className="tc">
          <button
            type="button"
            className="underline fw-medium stone-700 font-14 mb6"
            onClick={() => onShowMoreClick()}
          >
            Show more
          </button>
        </div>
      )}
      <div className="tc">
        <button
          className={classnames(
            'font-16 ba hover-b--midnight br12 secondary ph4 pv3 bg-white',
            isOptionSelected({ value: NO_PREFERENCE_VALUE })
              ? 'b--stone-700 stone-700'
              : 'b--default'
          )}
          type="button"
          onClick={() => setSelectedLitter(NO_PREFERENCE_VALUE)}
        >
          I’m open to any upcoming litter
        </button>
      </div>
    </div>
  );
}

LitterSelectorQuestion.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(LitterOptionPropType).isRequired
};
